import { z } from 'zod';
import { variaveisDesconhecidas } from './render';
import { listaClientesQuery } from './schemas';

// E-mail pelo CRM (specs/15-email.md): templates, caixa do vendedor, envio e configuração da empresa.

export const CANAIS = ['WHATSAPP', 'EMAIL'] as const;
export type Canal = (typeof CANAIS)[number];

export const CATEGORIAS_TEMPLATE = ['Prospecção', 'Comercial', 'Follow-up', 'Reativação', 'Pós-venda'] as const;

/** Limites de tamanho por canal (o wa.me corta mensagens longas; e-mail aceita mais). */
export const LIMITE_CORPO: Record<Canal, number> = { WHATSAPP: 1000, EMAIL: 50000 }; // e-mail pode ser HTML do editor (specs/25)

const semVariavelDesconhecida = (campo: string) => (v: string | null | undefined, ctx: z.RefinementCtx) => {
  const d = v ? variaveisDesconhecidas(v) : [];
  if (d.length) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [campo], message: `Variáveis desconhecidas: ${d.join(', ')}` });
};

const templateBase = z
  .object({
    nome: z.string().trim().min(2).max(80),
    canal: z.enum(CANAIS),
    categoria: z.preprocess((v) => (v === '' ? null : v), z.string().trim().min(1).max(40).nullable().optional()),
    assunto: z.preprocess((v) => (v === '' ? null : v), z.string().trim().min(1).max(200).nullable().optional()),
    corpo: z.string().trim().min(1).max(LIMITE_CORPO.EMAIL),
    /** Botão "Falar no WhatsApp" (só e-mail, specs/24): copiado para a campanha; usado no e-mail pela ficha */
    whatsappBotao: z.boolean().optional(),
    whatsappTexto: z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(40).nullable().optional()),
    whatsappMensagem: z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(500).nullable().optional()),
    whatsappStatusId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable().optional()),
    whatsappProtegeAtivo: z.boolean().optional(),
  })
  .strict();

function validarTemplate(d: Partial<z.infer<typeof templateBase>>, ctx: z.RefinementCtx) {
  if (d.canal === 'EMAIL' && d.assunto === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['assunto'], message: 'Informe o assunto do e-mail.' });
  }
  if (d.canal && d.corpo && d.corpo.length > LIMITE_CORPO[d.canal]) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['corpo'], message: `Máximo de ${LIMITE_CORPO[d.canal]} caracteres para ${d.canal === 'EMAIL' ? 'e-mail' : 'WhatsApp'}.` });
  }
  semVariavelDesconhecida('corpo')(d.corpo, ctx);
  semVariavelDesconhecida('assunto')(d.assunto, ctx);
  semVariavelDesconhecida('whatsappTexto')(d.whatsappTexto, ctx);
  semVariavelDesconhecida('whatsappMensagem')(d.whatsappMensagem, ctx);
}

export const templateCreateSchema = templateBase.superRefine((d, ctx) => {
  if (d.canal === 'EMAIL' && !d.assunto) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['assunto'], message: 'Informe o assunto do e-mail.' });
  }
  validarTemplate(d, ctx);
});
export const templateUpdateSchema = templateBase.partial().strict().superRefine(validarTemplate);
export const listaTemplatesQuery = z.object({ canal: z.enum(CANAIS).optional() }).strict();

// ----------------------------------------------------------------------------- caixa do vendedor

export const PROVEDORES_EMAIL = ['GOOGLE', 'MICROSOFT', 'LOCAWEB', 'OUTRO'] as const;
export type ProvedorEmail = (typeof PROVEDORES_EMAIL)[number];

/** Servidores padrão por provedor (o vendedor pode ajustar). */
export const PRESETS_PROVEDOR: Record<ProvedorEmail, {
  nome: string; smtpHost: string; smtpPorta: number; smtpSeguranca: 'SSL' | 'STARTTLS'; imapHost: string; imapPorta: number; dica: string;
}> = {
  GOOGLE: {
    nome: 'Gmail / Google Workspace', smtpHost: 'smtp.gmail.com', smtpPorta: 465, smtpSeguranca: 'SSL', imapHost: 'imap.gmail.com', imapPorta: 993,
    dica: 'Use uma “senha de app” do Google (Conta Google › Segurança › Verificação em duas etapas › Senhas de app). A senha normal não funciona.',
  },
  MICROSOFT: {
    nome: 'Outlook / Microsoft 365', smtpHost: 'smtp.office365.com', smtpPorta: 587, smtpSeguranca: 'STARTTLS', imapHost: 'outlook.office365.com', imapPorta: 993,
    dica: 'O administrador do Microsoft 365 precisa liberar “SMTP autenticado” e IMAP para a sua caixa.',
  },
  LOCAWEB: {
    nome: 'Locaweb', smtpHost: 'email-ssl.com.br', smtpPorta: 465, smtpSeguranca: 'SSL', imapHost: 'email-ssl.com.br', imapPorta: 993,
    dica: 'Usuário é o e-mail completo; a senha é a da sua caixa na Locaweb.',
  },
  OUTRO: {
    nome: 'Outro (SMTP/IMAP)', smtpHost: '', smtpPorta: 587, smtpSeguranca: 'STARTTLS', imapHost: '', imapPorta: 993,
    dica: 'Peça os dados de SMTP e IMAP a quem administra o e-mail da empresa.',
  },
};

const host = z.string().trim().toLowerCase().min(3).max(255).regex(/^[a-z0-9.-]+$/, 'Servidor inválido.');
/** Portas aceitas: só as de e-mail (o servidor do CRM não conecta em portas arbitrárias). */
export const PORTAS_SMTP = [465, 587, 25, 2525] as const;
export const PORTAS_IMAP = [993] as const;
const portaDe = (lista: readonly number[], rotulo: string) =>
  z.coerce.number().int().refine((p) => lista.includes(p), `Porta ${rotulo} deve ser ${lista.join(', ')}.`);
const emailCampo = z.string().trim().toLowerCase().email('E-mail inválido.').max(150);

/** Imagem enviada pelo editor (corpo do e-mail) ou para a assinatura (specs/25). */
export const IMAGEM_EMAIL_MAX_BYTES = 1_048_576;
export const imagemEmailSchema = z
  .object({ dataUrl: z.string().max(1_500_000), largura: z.number().int().min(1).max(4000).optional() })
  .strict();

/** Assinatura do vendedor: texto e imagem (tamanho e posição). Salva sem exigir novo teste da caixa. */
export const assinaturaEmailSchema = z
  .object({
    assinatura: z.preprocess((v) => (v === '' ? null : v), z.string().max(2000).nullable()),
    imagemId: z.string().uuid().nullable(),
    largura: z.union([z.literal(120), z.literal(180), z.literal(240)]).default(180),
    posicao: z.enum(['LADO', 'ACIMA']).default('LADO'),
  })
  .strict();

export const caixaEmailSchema = z
  .object({
    provedor: z.enum(PROVEDORES_EMAIL),
    nomeRemetente: z.string().trim().min(1).max(150),
    email: emailCampo,
    smtpHost: host,
    smtpPorta: portaDe(PORTAS_SMTP, 'SMTP'),
    smtpSeguranca: z.enum(['SSL', 'STARTTLS']),
    /** Sem IMAP o CRM envia, mas não registra as respostas */
    lerRespostas: z.boolean().default(true),
    imapHost: z.preprocess((v) => (v === '' ? null : v), host.nullable().optional()),
    imapPorta: z.preprocess((v) => (v === '' ? null : v), portaDe(PORTAS_IMAP, 'IMAP').nullable().optional()),
    usuario: z.string().trim().min(1).max(255),
    /** Em edição, ausente = mantém a senha atual */
    senha: z.string().min(1).max(500).optional(),
    assinatura: z.preprocess((v) => (v === '' ? null : v), z.string().max(2000).nullable().optional()),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (d.lerRespostas && (!d.imapHost || !d.imapPorta)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['imapHost'], message: 'Informe o servidor IMAP para ler as respostas.' });
    }
  });

export const emailConfigSchema = z
  .object({
    canalPadrao: z.enum(CANAIS),
    limiteDiario: z.coerce.number().int().min(1).max(2000),
    /** Envio em massa (campanhas): no máximo N e-mails por hora por caixa */
    limiteHora: z.coerce.number().int().min(1).max(1000),
    registrarRespostas: z.boolean(),
    retornoResposta: z.boolean(),
    /** Clique no WhatsApp do e-mail avulso leva o cliente a este status (null = não muda) */
    whatsappStatusId: z.string().uuid().nullable(),
    /** ...exceto quem já está num status do grupo ATIVO */
    whatsappProtegeAtivo: z.boolean(),
  })
  .partial()
  .strict();

// ----------------------------------------------------------------------------- envio

export const ANEXOS_MAX = 3;

/** Botão "Falar no WhatsApp" no e-mail (specs/24): textos padrão (aceitam as variáveis). */
export const WHATSAPP_BOTAO_PADRAO = 'Falar com {{vendedor}} no WhatsApp';
export const WHATSAPP_MENSAGEM_PADRAO = 'Olá {{vendedor}}, recebi seu e-mail e gostaria de conversar.';
export const ANEXOS_MAX_BYTES = 5 * 1_048_576;

export const enviarEmailSchema = z
  .object({
    assunto: z.string().trim().min(1).max(200),
    corpo: z.string().trim().min(1).max(LIMITE_CORPO.EMAIL),
    cc: z.array(emailCampo).max(5).default([]),
    templateId: z.string().uuid().optional(),
    /** Resposta a um e-mail recebido (mantém a conversa no mesmo fio) */
    respostaA: z.string().uuid().optional(),
    anexos: z
      .array(z.object({ nome: z.string().trim().min(1).max(150), dataUrl: z.string().max(8 * 1_048_576) }).strict())
      .max(ANEXOS_MAX)
      .default([]),
    /** Cria um retorno se o cliente não responder em N dias */
    retornoDias: z.number().int().min(1).max(60).optional(),
    /** Botão "Falar no WhatsApp" com o número do vendedor; o clique é registrado (specs/24) */
    botaoWhatsapp: z.boolean().default(false),
    /** Incluir a assinatura do vendedor (texto e imagem) neste envio (specs/25) */
    assinatura: z.boolean().default(true),
  })
  .strict()
  .superRefine((d, ctx) => {
    semVariavelDesconhecida('assunto')(d.assunto, ctx);
    semVariavelDesconhecida('corpo')(d.corpo, ctx);
  });

// ----------------------------------------------------------------------------- envio em massa (lista de clientes)

export const EMAIL_MASSA_MAX = 5000;

/** Clientes marcados um a um OU todos os que a lista encontra com os filtros atuais. */
export const emailMassaAlvoSchema = z
  .object({
    clienteIds: z.array(z.string().uuid()).min(1, 'Selecione ao menos um cliente.').max(EMAIL_MASSA_MAX).optional(),
    filtros: listaClientesQuery.omit({ page: true, limit: true, sort: true }).partial().strict().optional(),
    /** VENDEDOR_DO_CLIENTE = cada e-mail sai pela caixa do vendedor do cliente; EU = todos pela minha caixa */
    remetente: z.enum(['VENDEDOR_DO_CLIENTE', 'EU']).default('EU'),
  })
  .strict();

export const emailMassaSchema = emailMassaAlvoSchema
  .extend({
    assunto: z.string().trim().min(1).max(200),
    corpo: z.string().trim().min(1).max(LIMITE_CORPO.EMAIL),
    templateId: z.string().uuid().optional(),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (!!d.clienteIds === !!d.filtros) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Envie os clientes selecionados ou os filtros da lista.' });
    semVariavelDesconhecida('assunto')(d.assunto, ctx);
    semVariavelDesconhecida('corpo')(d.corpo, ctx);
  });

export const emailMassaPreviaSchema = emailMassaAlvoSchema.superRefine((d, ctx) => {
  if (!!d.clienteIds === !!d.filtros) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Envie os clientes selecionados ou os filtros da lista.' });
});
